import { Injectable } from '@angular/core';
import { ListDetailManagementService } from '@app/lists/shared/list-detail-management/services/list-detail-management.service';
import {
  ActionSheetController,
  ModalController,
  PopoverController,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState } from '@panamax/app-state';
import {
  ListAnalyticsService,
  MasterListItemProductState,
} from '@usf/ngrx-list';
import { createMasterListItemProductKey } from '../../../lists/pages/master-list/selectors/helpers/master-list.selectors.helpers';
import { MasterListService } from '../../../lists/pages/master-list/services/master-list.service';
import { ListsOverlayService } from '../../../lists/services/lists-overlay.service';
import { ProductRow } from '../../../lists/shared/list-detail-management/model/list-detail-management-view.model';
import { ProductAlternativeSearchService } from '../../../product-detail/services/product-alternative-search.service';
import { ProductDetailTagsService } from '../../../product-detail/services/product-detail-tags.service';
import { NavigationHelperService } from '../../helpers/navigation.helpers.service';
import { EllipsisClickData } from '../../models/usf-product-card.model';
import { ModalService } from '../modal/modal.service';
import { ProductCardService } from './product-card.service';
import { QUICK_VIEW_SEGMENT } from '../../constants/lists-constants';
import { ProductPropertiesEnum } from '@usf/product-types';
import { List } from '@usf/list-types';
import { ProductAnalyticsService } from '@usf/ngrx-product';
import { ToggleNewSearchService } from '@shared/services/toggle-new-search/toggle-new-search.service';
import { SEARCH_PAGES_ENUM } from '@shared/constants/search-pages.enum';

@Injectable({
  providedIn: 'root',
})
export class MasterListProductCardService extends ProductCardService {
  constructor(
    actionSheetController: ActionSheetController,
    modalController: ModalController,
    popoverController: PopoverController,
    panAppState: PanAppState,
    translateService: TranslateService,
    listsOverlayService: ListsOverlayService,
    modalService: ModalService,
    navigationHelperService: NavigationHelperService,
    productAlternativeSearchService: ProductAlternativeSearchService,
    productDetailTagsService: ProductDetailTagsService,
    private masterListService: MasterListService,
    private listAnalyticsService: ListAnalyticsService,
    listDetailManagementService: ListDetailManagementService,
    analyticsService: ProductAnalyticsService,
    toggleNewSearchService: ToggleNewSearchService,
  ) {
    super(
      actionSheetController,
      modalController,
      popoverController,
      panAppState,
      translateService,
      listsOverlayService,
      modalService,
      navigationHelperService,
      productAlternativeSearchService,
      productDetailTagsService,
      listDetailManagementService,
      analyticsService,
      toggleNewSearchService,
    );
  }

  constructEllipsisButtons(
    ellipsisClickData: EllipsisClickData,
    isRestrictedToMsl?: boolean,
  ) {
    let buttons = [];
    // Master list specific buttons
    const masterListProductRow = ellipsisClickData?.product;
    if (
      this.shouldAddShowAlternatesBtn(masterListProductRow, isRestrictedToMsl)
    ) {
      buttons.push(
        this.getShowAlternatesBtn(
          ellipsisClickData?.isTouch,
          masterListProductRow,
        ),
      );
    } else if (
      this.shouldAddHideAlternatesBtn(masterListProductRow, isRestrictedToMsl)
    ) {
      buttons.push(
        this.getHideAlternatesBtn(
          ellipsisClickData?.isTouch,
          masterListProductRow,
        ),
      );
    }
    // Normal product card buttons
    if (
      this.shouldAddSearchForSubs(ellipsisClickData.product) &&
      !ellipsisClickData.product?.summary?.properties?.has(
        ProductPropertiesEnum.discontinued,
      )
    ) {
      buttons.push(
        this.getSearchForSubsBtn(
          ellipsisClickData,
          SEARCH_PAGES_ENUM.listDetail,
        ),
      );
    }
    buttons.push(
      this.getQuickViewBtn(ellipsisClickData, SEARCH_PAGES_ENUM.listDetail),
    );
    const addedCompareWithAlternatesBtn = this.getCompareWithAlternatesBtn(
      buttons,
      ellipsisClickData,
      isRestrictedToMsl,
    );
    if (this.shouldAddProductStatus(ellipsisClickData.product)) {
      buttons.push(this.getProductStatusBtn(ellipsisClickData));
    }
    this.getSellerQuickViewBtn(buttons, ellipsisClickData);
    if (!addedCompareWithAlternatesBtn) {
      this.getCompareBtn(buttons, ellipsisClickData);
    }
    return buttons;
  }

  setCompareWithAlternatesModeForItem(
    item: MasterListItemProductState,
    value: boolean,
  ) {
    const key = createMasterListItemProductKey(
      item.listId,
      item.listGroupId,
      item.listItemId,
      item.listItemProductId,
    );
    this.masterListService.setCompareWithAlternatesModeForItem(key, value);
  }

  shouldAddCompareWithAlternatesBtn(
    ellipsisClickData: EllipsisClickData,
    isRestrictedToMsl: boolean,
  ) {
    return (
      !isRestrictedToMsl &&
      ellipsisClickData?.mslCompareAlternatesFeatureFlag &&
      ellipsisClickData?.product?.isPrimaryProduct &&
      ellipsisClickData?.product?.masterListItem?.isShowingAlternates &&
      ellipsisClickData?.product?.hasAlternatesToShow
    );
  }

  getCompareWithAlternatesBtn(
    buttons,
    ellipsisClickData: EllipsisClickData,
    isRestrictedToMsl?: boolean,
  ) {
    if (
      this.shouldAddCompareWithAlternatesBtn(
        ellipsisClickData,
        isRestrictedToMsl,
      )
    ) {
      const button = {
        text: this.translateService.instant(
          'i18n.search.searchCompare.compareWithAlternatesFromEllipsis',
        ),
        handler: () => {
          this.masterListService.resetCompareWithAlternatesSelections();
          this.setCompareWithAlternatesModeForItem(
            ellipsisClickData.product.masterListItem,
            true,
          );
          this.dismissProductCardOptions(ellipsisClickData.isTouch);
        },
      };
      buttons.push(button);
      return true;
    }
    return false;
  }

  toggleAlternates(masterListProductRow: ProductRow, showAlternates: boolean) {
    const masterListItemProductKey = createMasterListItemProductKey(
      masterListProductRow.masterListItem.listId,
      masterListProductRow.masterListItem.listGroupId,
      masterListProductRow.masterListItem.listItemId,
      masterListProductRow.masterListItem.listItemProductId,
    );
    this.masterListService.setShowAlternates(
      [masterListItemProductKey],
      showAlternates,
    );
  }

  private shouldAddShowAlternatesBtn(
    masterListProductRow: ProductRow,
    isRestrictedToMsl: boolean,
  ) {
    return (
      masterListProductRow?.hasAlternatesToShow &&
      !masterListProductRow?.masterListItem?.isShowingAlternates &&
      !isRestrictedToMsl
    );
  }

  private shouldAddHideAlternatesBtn(
    masterListProductRow: ProductRow,
    isRestrictedToMsl: boolean,
  ) {
    return (
      masterListProductRow?.hasAlternatesToShow &&
      masterListProductRow?.masterListItem.isShowingAlternates &&
      !isRestrictedToMsl
    );
  }

  private getShowAlternatesBtn(
    isTouch: boolean,
    masterListProductRow: ProductRow,
  ) {
    return {
      text: this.translateService.instant('i18n.lists.showAlternateProducts'),
      handler: () => {
        this.toggleAlternates(masterListProductRow, true);
        this.dismissProductCardOptions(isTouch);
      },
    };
  }

  private getHideAlternatesBtn(
    isTouch: boolean,
    masterListProductRow: ProductRow,
  ) {
    return {
      text: this.translateService.instant('i18n.lists.hideAlternateProducts'),
      handler: () => {
        this.toggleAlternates(masterListProductRow, false);
        if (masterListProductRow.masterListItem?.isComparingWithAlternates) {
          this.masterListService.cancelCompareWithAlternatesMode();
        }
        this.dismissProductCardOptions(isTouch);
      },
    };
  }

  async openQuickViewModal(
    ellipsisClickData: EllipsisClickData,
    selectedSegment: string,
    originSearchPage?: string,
  ) {
    const modal = await this.getQuickViewModal(
      ellipsisClickData,
      selectedSegment,
      originSearchPage,
    );

    const quickViewModal = await this.modalController.create(modal);

    quickViewModal.onDidDismiss().then((resp: any) => {
      this.quickViewClosedAnalyticEvent(ellipsisClickData);

      if (!!resp?.data) {
        this.routeToPDP(resp.data, true);
      }
    });
    return await quickViewModal.present();
  }

  quickViewClosedAnalyticEvent(ellipsisClickData: EllipsisClickData) {
    let products: any[] = [];
    if (ellipsisClickData.product?.inventory?.isInStock) {
      products.push({
        attributes: ellipsisClickData.product.trackingAttributes,
        divisionApn: `${ellipsisClickData.product.summary.divisionNumber}-${ellipsisClickData.product.productNumber}`,
        primaryProduct: 1,
      });
    } else {
      let product: any = {
        attributes: ellipsisClickData.product.trackingAttributes,
        divisionApn: `${ellipsisClickData.product.summary.divisionNumber}-${ellipsisClickData.product.productNumber}`,
        primaryProduct: 1,
        exceptionView: '1',
      };
      if (ellipsisClickData.product?.hasAlternative)
        product = { ...product, exceptionNumberOfSubs: 1 };
      products.push(product);
    }

    if (
      ellipsisClickData.product?.hasAlternative &&
      ellipsisClickData.product?.alternative?.product
    ) {
      products.push({
        attributes:
          ellipsisClickData.product.alternative.product.trackingAttributes,
        divisionApn: `${ellipsisClickData.product.alternative.product.summary.divisionNumber}-${ellipsisClickData.product.alternative.product.productNumber}`,
        substituteView: '1',
        substitutionParentApn: `${ellipsisClickData.product.summary.divisionNumber}-${ellipsisClickData.product.productNumber}`,
      });
    }

    this.listAnalyticsService.trackQuickViewClosed(products);
  }

  getQuickViewModal(
    ellipsisClickData: EllipsisClickData,
    selectedSegment: string,
    originSearchPage?: string,
  ) {
    if (selectedSegment === QUICK_VIEW_SEGMENT)
      this.quickViewOpenedAnalyticEvent(ellipsisClickData);
    return super.getQuickViewModal(
      ellipsisClickData,
      selectedSegment,
      originSearchPage,
    );
  }

  quickViewOpenedAnalyticEvent(ellipsisClickData: EllipsisClickData) {
    let products: any[] = [];
    if (ellipsisClickData.product?.inventory?.isInStock) {
      products.push({
        attributes: ellipsisClickData.product.trackingAttributes,
        divisionApn: `${ellipsisClickData.product.summary.divisionNumber}-${ellipsisClickData.product.productNumber}`,
        primaryProduct: 1,
      });
    } else {
      let product: any = {
        attributes: ellipsisClickData.product.trackingAttributes,
        divisionApn: `${ellipsisClickData.product.summary.divisionNumber}-${ellipsisClickData.product.productNumber}`,
        primaryProduct: 1,
        exceptionView: '1',
      };
      if (ellipsisClickData.product?.hasAlternative)
        product = { ...product, exceptionNumberOfSubs: 1 };
      products.push(product);
    }

    if (
      ellipsisClickData.product?.hasAlternative &&
      ellipsisClickData.product?.alternative?.product
    ) {
      products.push({
        attributes:
          ellipsisClickData.product.alternative.product.trackingAttributes,
        divisionApn: `${ellipsisClickData.product.alternative.product.summary.divisionNumber}-${ellipsisClickData.product.alternative.product.productNumber}`,
        substituteView: '1',
        substitutionParentApn: `${ellipsisClickData.product.summary.divisionNumber}-${ellipsisClickData.product.productNumber}`,
      });
    }

    this.listAnalyticsService.trackQuickViewOpen(products);
  }

  directlyOpenViewNote(ellipsisClickData: EllipsisClickData) {
    this.listsOverlayService.openCreateEditNoteModal(
      false,
      ellipsisClickData.product.item,
      ellipsisClickData.list as List,
      ellipsisClickData.product.masterListItem.referenceComment,
    );
  }
}
